import { Component, ViewChild } from '@angular/core';
import { HeaderService } from '@app/core/services/header.service';
import { ModalService } from '@app/core/services/modal.service';
import { ODataService } from '@app/core/services/odata/odata.service';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { dashboardColumns } from '@shared/columns/dashboard.columns';
import { queryDashboardTableConfig } from '@views/dashboard/config/query.dashboard-config';
import {
  AddMeterSourceModalComponent
} from '@shared/modals/meteringpoint/source/add-meter-source-modal/add-meter-source-modal.component';
import { ConnectModalComponent } from '@shared/modals/meteringpoint/connect-modal/connect-modal.component';
import { TableComponent } from '@shared/components/table/table.component';
import { SortParam } from '@app/core/constants/sort-param';
import { Observable } from 'rxjs';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { ODataType } from '@app/core/services/odata/types/odata-type';
import { SortDir } from '@app/core/constants/sort-dir';
import { ODataQueryBuilder } from '@app/core/services/odata/builders/odata-query-builder.class';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DataHubStatus, MeteringPointType } from '@app/api/dmc';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { CardComponent } from '@shared/components/card/card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard-meteringpoint-with-supplier',
  imports: [
    TableComponent,
    ReactiveFormsModule,
    CardComponent,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    SelectDirective,
    ButtonDirective,
    TranslatePipe,
  ],
  templateUrl: './dashboard-meteringpoint-with-supplier.component.html',
  styleUrl: './dashboard-meteringpoint-with-supplier.component.css',
})
export class DashboardMeteringpointWithSupplierComponent {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  public searchForm = new FormGroup({
    meteringPointIdentification: new FormControl<string | null>(null),
    address: new FormControl<string | null>(null),
    typeOfMeteringPoint: new FormControl<MeteringPointType | null>(null),
    dataHubStatus: new FormControl<DataHubStatus | null>(null),
  });

  public meteringPointTypeOptions: KeyValue[] = enumToKeyValue(MeteringPointType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.meteringpointtype.' });
  public dataHubStatusOptions: KeyValue[] = enumToKeyValue(DataHubStatus, { omitKeys: ['NotSpecified'], prefixValue: 'enum.datahub.status.' });

  public actions: MenuAction[] = [
    {
      id: 'createMeter',
      title: 'meteringpoint.source.addmeter.title',
      callback: () => this.openCreateMeterModal(),
      disabled: () => this.selectedElements.length !== 1,
    },
    {
      id: 'connect',
      title: 'common.connect',
      callback: () => this.openConnectModal(),
      disabled: () => this.selectedElements.length !== 1,
    }
  ];

  public tableConfig: TableConfig<any> = {
    id: 'table-dashboard-new-metering-point-with-supplier',
    activeColumns: ['meteringPointIdentification', 'address', 'physicalStatusOfMeteringPoint', 'typeOfMeteringPoint', 'subTypeOfMeteringPoint', 'meterNumber', 'dataHubStatus', 'balanceSupplierDate'],
    oData: true,
    serviceCallback: (pageSize: number, page: number, sort?: SortParam) => this.search(pageSize, page, sort),
    pageSizeSelection: true,
    pageSizeOptions: [{ key: '25', value: '25' }, { key: '50', value: '50' }, { key: '100', value: '100' }, { key: '500', value: '500' }, { key: '1000', value: '1000' }],
    contextActions: this.actions,
    columns: dashboardColumns,
    ...queryDashboardTableConfig,
  }

  public selectedElements: any[] = [];

  constructor(
    private readonly headerService: HeaderService,
    private readonly modalService: ModalService,
    private readonly oDataService: ODataService,
  ) {
    this.headerService.setTitle('dashboard.query.newmeteringpointwithsupplier.title');

    this.headerService.setActions(this.actions)
  }

  public search(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    const fixedSort: SortParam = {
      sortField: 'typeOfMeteringPoint',
      sortDirection: SortDir.desc
    };

    const dateSort: SortParam = {
      sortField: 'balanceSupplierDate',
      sortDirection: SortDir.desc,
    };

    const includeFixed = sort?.sortField !== 'typeOfMeteringPoint';
    const includeDate = sort == null;
    let includeUserSort: string[] = [];

    if (sort != null) {
      if (sort.sortField === 'meterNumber') {
        includeUserSort = ['meterNumber ne null desc', `meterNumber ${sort.sortDirection}`];
      } else {
        includeUserSort = [`${sort.sortField} ${sort.sortDirection}`];
      }
    }

    const orderByParts = [
      includeFixed ? `${fixedSort.sortField} ${fixedSort.sortDirection}` : null,
      includeDate ? `${dateSort.sortField} ${dateSort.sortDirection}` : null,
      ...includeUserSort,
    ].filter(Boolean).join(', ');

    const filter = this.oDataService.filter();
    const { meteringPointIdentification, address, typeOfMeteringPoint, dataHubStatus } = this.searchForm.value;

    if (meteringPointIdentification) {
      filter.contains('meteringPointIdentification', meteringPointIdentification);
    }
    if (address) {
      filter.contains('address', address);
    }
    if (typeOfMeteringPoint) {
      filter.equals('typeOfMeteringPoint', typeOfMeteringPoint);
    }
    if (dataHubStatus) {
      filter.equals('dataHubStatus', dataHubStatus);
    }

    const query: ODataQueryBuilder = this.oDataService.dmc.query()
      .filter(filter)
      .paginate(page, pageSize)
      .count()
      .orderBy(orderByParts);

    return this.oDataService.dmc.getCollection(
      ODataType.newMeteringPointsWithSupplier,
      query
    );
  }

  public performSearch(): void {
    this.table?.search();
  }

  public clearForm(): void {
    this.searchForm.reset();
    this.table?.search();
  }

  public openConnectModal(): void {
    this.modalService.show({
      component: ConnectModalComponent,
      title: 'common.connect',
      actions: [
        { type: 'primary', label: 'common.connect', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        element: this.selectedElements[0],
      }
    }).subscribe(() => {
      this.table?.search();
    })
  }

  public openCreateMeterModal(): void {
    this.modalService.show({
      component: AddMeterSourceModalComponent,
      title: 'meteringpoint.source.addmeter.title',
      actions: [
        { type: 'primary', label: 'common.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        element: this.selectedElements[0],
      }
    }).subscribe(() => {
      this.table?.search();
    })
  }
}
